import { Fragment, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import CompagniaSmartField, { useCompPool } from "./CompagniaSmartField";
import { guardCreate } from "../context/DuplicateGuard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import MultiCellulare from "./MultiCellulare";
import { Label } from "./ui/label";
import { Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SimilarContacts from "./SimilarContacts";
import TitoloInput from "./TitoloInput";

const FIELDS = [
  { key: "denominazione", label: "Denominazione / Ragione sociale", w: 2, required: true },
  { key: "codice_ivass", label: "Codice ANIA" },
  { key: "partita_iva", label: "Partita IVA" },
  { key: "codice_fiscale", label: "Codice Fiscale" },
  { key: "referente", label: "Referente" },
  { key: "indirizzo", label: "Indirizzo", w: 2 },
  { key: "numero_civico", label: "N° Civ." },
  { key: "comune", label: "Comune" },
  { key: "provincia", label: "Provincia" },
  { key: "cap", label: "CAP" },
  { key: "telefono", label: "Telefono" },
  { key: "fax", label: "Fax" },
  { key: "cellulare", label: "Cellulare" },
  { key: "email", label: "Email" },
  { key: "pec", label: "PEC" },
  { key: "sito_web", label: "Sito Web" },
  { key: "note", label: "Note", w: 2 },
];

// Modale di creazione di una nuova compagnia assicurativa (campi base), precompilata
// con il nome digitato. Dopo il salvataggio richiama onSaved(record). Le sottosezioni
// Ispettorati/Agenzie si gestiscono dalla scheda della compagnia, non da qui.
export default function NuovaCompagniaDialog({ open, onOpenChange, initialName = "", onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [comps, setComps] = useState([]);
  const pool = useCompPool(comps);
  useEffect(() => { if (open) api.get("/anagrafiche/compagnie").then(({ data }) => setComps(data || [])).catch(() => setComps([])); }, [open]);

  useEffect(() => { if (open) setForm({ denominazione: initialName || "" }); }, [open, initialName]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    const den = (form.denominazione || "").trim();
    if (!den) { toast.error("La denominazione è obbligatoria"); return; }
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, typeof v === "string" ? v.trim() : (v ?? "")]));
      payload.denominazione = den;
      const { record } = await guardCreate("compagnie", payload);
      toast.success("Compagnia creata");
      onSaved && onSaved(record);
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="nuova-compagnia-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <Building2 className="h-5 w-5 text-amber-400" /> Nuova Compagnia assicurativa
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-4" data-testid="nuova-compagnia-form">
          <div>
            <Label className="text-slate-300">Titolo</Label>
            <TitoloInput value={form.titolo} onChange={(v) => setField("titolo", v)} testId="nc-titolo" className="mt-1.5 bg-slate-900/60 border-slate-700" />
          </div>
          <div />
          {FIELDS.map((f) => (
            <Fragment key={f.key}>
            <div className={f.w === 2 ? "sm:col-span-2" : ""}>
              <Label className="text-slate-300">{f.label}{f.required ? " *" : ""}</Label>
              {["comune", "provincia", "cap", "indirizzo"].includes(f.key) ? (
                <CompagniaSmartField fkey={f.key} form={form} setForm={setForm} pool={pool} testId={`nc-${f.key}`} />
              ) : f.key === "cellulare" ? (
                <div className="mt-1.5"><MultiCellulare extra={form.cellulari_extra} onExtra={(v) => setField("cellulari_extra", v)} labels={form.cellulari_etichette} onLabels={(v) => setField("cellulari_etichette", v)} testId="nc-cellulare">
                  <Input value={form.cellulare || ""} onChange={(e) => setField("cellulare", e.target.value)} data-testid="nc-cellulare" className="bg-slate-900/60 border-slate-700" />
                </MultiCellulare></div>
              ) : ["note", "telefono", "fax", "email", "pec"].includes(f.key) ? (
                <Input value={form[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)}
                  data-testid={`nc-${f.key}`} className="mt-1.5 bg-slate-900/60 border-slate-700" />
              ) : (
                <CompagniaSmartField fkey={f.key} form={form} setForm={setForm} pool={pool} testId={`nc-${f.key}`} />
              )}
            </div>
            {f.key === "denominazione" && (
              <div className="sm:col-span-2"><SimilarContacts atype="compagnie" denominazione={form.denominazione} onSelect={(r) => { onSaved && onSaved(r); onOpenChange(false); }} onCancel={() => onOpenChange(false)} testId="nc-similar" /></div>
            )}
            </Fragment>
          ))}
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="nc-cancel"
              className="border-slate-700 text-slate-200 hover:bg-slate-800">Annulla</Button>
            <Button type="submit" disabled={saving} data-testid="nc-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea compagnia"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
